import intersection from 'lodash/intersection';
import omit from 'lodash/omit';

import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import { convertMoneyToNumber } from '../../util/currency';
import { createResourceLocatorString } from '../../util/routes';
import {
  isAnyFilterActive,
  parseSelectFilterOptions,
  constructQueryParamName,
} from '../../util/search';
import { parse, stringify } from '../../util/urlHelpers';
import { listingPageLinkProps } from '../../util/listingSeoUrl';
import { findCategoryBySlug } from '../../util/categoryLinks';
import { getStartOf, parseDateFromISO8601, subtractTime } from '../../util/dates';
import { isFieldForCategory } from '../../util/fieldHelpers';
import {
  tireSizeLabel,
  rimSizeLabel,
  tireSizeToParams,
  rimSizeToParams,
} from '../../util/dimensionRoutes';
import { itemListSchema } from '../SeoPages/seoSchema';

const { Money } = sdkTypes;

/**
 * Omit those listing field parameters, that are not allowed with current category selection
 *
 * @param {Object} searchParams current search params
 * @param {Object} filterConfigs contains listingFieldsConfig and defaultFiltersConfig.
 * @returns search parameters without currently restricted listing fields
 */
export const omitLimitedListingFieldParams = (searchParams, filterConfigs) => {
  const { listingFieldsConfig, defaultFiltersConfig, listingCategories } = filterConfigs;
  const categorySearchConfig = defaultFiltersConfig.find(f => f.schemaType === 'category');
  const validNestedCategoryParamNames = categorySearchConfig
    ? validURLParamForCategoryData(categorySearchConfig.key, listingCategories, 1, searchParams)
    : {};

  return Object.entries(searchParams).reduce((picked, searchParam) => {
    const [searchParamKey, searchParamValue] = searchParam;
    const foundConfig = listingFieldsConfig.find(
      f => constructQueryParamName(f.key, f.scope) === searchParamKey
    );
    const currentCategories = Object.values(validNestedCategoryParamNames);
    const isForCategory = isFieldForCategory(currentCategories, foundConfig);
    const searchParamMaybe =
      !foundConfig || (foundConfig && isForCategory) ? { [searchParamKey]: searchParamValue } : {};
    return { ...picked, ...searchParamMaybe };
  }, {});
};

/**
 * Validates a filter search param against the default and extended data configuration of listings.
 *
 * All invalid param names and values are dropped
 *
 * @param {String} queryParamName Search parameter name
 * @param {Object} paramValue Search parameter value
 * @param {Object} listingFieldFilters extended data configuration with indexForSearch === true
 * @param {Object} defaultFilters configuration for default built-in filters.
 */
export const validURLParamForExtendedData = (
  queryParamName,
  paramValueRaw,
  listingFieldFilters,
  defaultFilters
) => {
  const paramValue = paramValueRaw.toString();

  // Price is built-in filter for listing entities
  if (queryParamName === 'price') {
    // Restrict price range to correct min & max
    const { min, max } = defaultFilters.find(conf => conf.schemaType === 'price') || {};
    const valueArray = paramValue ? paramValue.split(',') : [];
    const validValues = valueArray.map(v => {
      return v < min ? min : v > max ? max : v;
    });
    return validValues.length === 2 ? { [queryParamName]: validValues.join(',') } : {};
  } else if (queryParamName === 'keywords') {
    return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
  } else if (queryParamName === 'dates') {
    const searchTZ = 'Etc/UTC';
    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const dates = paramValue ? paramValue.split(',') : [];
    const hasValues = dates.length > 0;
    const startDate = hasValues ? parseDateFromISO8601(dates[0], searchTZ) : null;
    const endDate = hasValues ? parseDateFromISO8601(dates[1], searchTZ) : null;
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    return hasValidDates ? { [queryParamName]: paramValue } : {};
  }

  // Resolve configurations for extended data filters
  const listingFieldFilterConfig = listingFieldFilters.find(
    f => queryParamName === constructQueryParamName(f.key, f.scope)
  );

  if (listingFieldFilterConfig) {
    const { schemaType, enumOptions = [], filterConfig } = listingFieldFilterConfig;
    if ([SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM].includes(schemaType)) {
      const isSchemaTypeMultiEnum = schemaType === SCHEMA_TYPE_MULTI_ENUM;
      const searchMode = filterConfig?.searchMode;

      // Pick valid select options only
      const valueArray = parseSelectFilterOptions(paramValue);
      const allowedValues = enumOptions.map(o => `${o.option}`);
      const validValues = intersection(valueArray, allowedValues).join(',');

      return validValues.length > 0
        ? {
          [queryParamName]:
            isSchemaTypeMultiEnum && searchMode ? `${searchMode}:${validValues}` : validValues,
        }
        : {};
    } else {
      // Generic filter - remove empty params
      return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
    }
  }
  return {};
};

const validURLParamForCategoryData = (prefix, categories, level, params) => {
  const levelKey = constructQueryParamName(`${prefix}${level}`, 'public');
  const levelValue = typeof params?.[levelKey] !== 'undefined' ? `${params?.[levelKey]}` : undefined;

  const foundCategory =
    levelValue && categories.find(cat => cat.id.toLowerCase() === levelValue.toLowerCase());
  const subcategories = foundCategory?.subcategories || [];

  return foundCategory && subcategories.length > 0
    ? {
      [levelKey]: foundCategory.id,
      ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
    }
    : foundCategory
      ? { [levelKey]: foundCategory.id }
      : {};
};


/**
 * Checks filter param value validity.
 *
 * The URL params that are not part of listing.query filters are dropped by default.
 *
 * @param {Object} params Search query params
 * @param {Object} filterConfigs contains listingFieldsConfig and defaultFiltersConfig.
 * @param {boolean} dropNonFilterParams if false, extra params are passed through.
 */
export const validFilterParams = (params, filterConfigs, dropNonFilterParams = true) => {
  const { listingFieldsConfig, defaultFiltersConfig, listingCategories } = filterConfigs;

  const listingFieldFiltersConfig = listingFieldsConfig.filter(
    config => config.filterConfig?.indexForSearch
  );
  const listingFieldParamNames = listingFieldFiltersConfig.map(f =>
    constructQueryParamName(f.key, f.scope)
  );
  // Note: builtInFilterParamNames might include categoryLevel,
  //       even though it isn't a paramname that's used with nested category tree.
  //       (pub_categoryLevel1, pub_categoryLevel2, and pub_categoryLevel3 are used instead.)
  const builtInFilterParamNames = defaultFiltersConfig.map(f => {
    return f.schemaType === 'category' ? `pub_${f.key}` : f.key;
  });
  const filterParamNames = [...listingFieldParamNames, ...builtInFilterParamNames];

  // Note: currently, we only support nested enums with a single default filter
  //       that has schema type: "category"
  const categorySearchConfig = defaultFiltersConfig.find(f => f.schemaType === 'category');
  const validNestedCategoryParamNames = categorySearchConfig
    ? validURLParamForCategoryData(categorySearchConfig.key, listingCategories, 1, params)
    : {};
  const isParamNameNestedEnumRelated = (paramName, key, isNestedEnum) => {
    return isNestedEnum && key ? paramName.indexOf(key) > -1 : false;
  };

  // search params without category-restricted params
  const unlimitedSearchParams = omitLimitedListingFieldParams(params, filterConfigs);
  const paramEntries = Object.entries(unlimitedSearchParams);

  const listingFieldsAndBuiltInFilterParamNames = paramEntries.reduce((validParams, entry) => {
    const [paramName, paramValue] = entry;
    const isIndependentParam = filterParamNames.includes(paramName);
    const isNestedEnum = isIndependentParam
      ? false
      : isParamNameNestedEnumRelated(
        paramName,
        categorySearchConfig?.key,
        categorySearchConfig?.isNestedEnum
      );
    return isIndependentParam
      ? {
        ...validParams,
        ...validURLParamForExtendedData(
          paramName,
          paramValue,
          listingFieldFiltersConfig,
          defaultFiltersConfig
        ),
      }
      : dropNonFilterParams || isNestedEnum
        ? { ...validParams }
        : { ...validParams, [paramName]: paramValue };
  }, {});

  // TODO: Currently this only supports categoryLevel with nested param names.
  //       This needs more work to make other enum fields to understand nested keys.
  return { ...listingFieldsAndBuiltInFilterParamNames, ...validNestedCategoryParamNames };
};

/**
 * Helper to pick only valid values of search params from URL (location)
 * Note: location.search might look like: '?pub_category=men&pub_amenities=towels,bathroom'
 *
 * @param {Object} props object containing: location and (app) config
 * @returns picked search params against extended data config and default filter config
 */
export const validUrlQueryParamsFromProps = props => {
  const { location, config, make, model, tireSize, rimSize } = props;
  const { listingFields: listingFieldsConfig } = config?.listing || {};
  const { defaultFilters: defaultFiltersConfig } = config?.search || {};
  const listingCategories = config.categoryConfiguration.categories;
  const filterConfigs = {
    listingFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
  };

  // eslint-disable-next-line no-unused-vars
  const { mapSearch, page, ...searchInURL } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const searchParams = {
    ...searchInURL,
    ...(make && { pub_categoryLevel1: make }),
    ...(model && { pub_categoryLevel2: model }),
    // On dimension landing pages the filters come from the path slug — inject
    // them so navigation (filter/sort/reset) preserves the dimension.
    ...(tireSize ? tireSizeToParams(tireSize) || {} : {}),
    ...(rimSize ? rimSizeToParams(rimSize) || {} : {}),
  };

  // urlQueryParams doesn't contain page specific url params
  // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
  return validFilterParams(searchParams, filterConfigs, false);
};


/**
 * Search params to restore when the user leaves map view for grid/list.
 *
 * Entering map view (with a saved ort and no location in the URL) drops the
 * user's `sort` and adds `origin=ort` so results are ranked by distance —
 * distance ranking and an explicit sort are mutually exclusive. On the way
 * back out we undo both: re-apply the sort that was removed and drop the
 * map-added origin.
 *
 * If the user picked a different sort while in map view, that sort is already
 * in the URL — we keep it and restore nothing.
 *
 * @param {Object} urlQueryParams current valid URL query params
 * @param {String|null} removedSort the sort value dropped when map view was entered
 * @returns {Object|null} the params to push, or null when there is nothing to restore
 */
export const restoreSortOnLeavingMap = (urlQueryParams, removedSort) => {
  if (!removedSort || urlQueryParams.sort) {
    return null;
  }
  return { ...omit(urlQueryParams, 'origin'), sort: removedSort };
};

/**
 * Helper to figure out initialValues for Final Form that handles search filters
 *
 * @param {Object} props object containing: location, listingFieldsConfig, defaultFiltersConfig
 * @param {Object} currentQueryParams object containing current state of queryParams (used only when isLiveEdit is false)
 * @returns a function with params queryParamNames, and isLiveEdit.
 *          It's called from FilterComponent and it returns initial values for the filter.
 */
export const initialValues = (props, currentQueryParams) => (queryParamNames, isLiveEdit) => {
  const urlQueryParams = validUrlQueryParamsFromProps(props);

  // Get initial value for a given parameter from state if its there.
  const getInitialValue = paramName => {
    // Query parameters that are in state (user might have not yet clicked "Apply")
    const currentQueryParam = currentQueryParams[paramName];
    const hasQueryParamInState = typeof currentQueryParam !== 'undefined';
    return hasQueryParamInState && !isLiveEdit ? currentQueryParam : urlQueryParams[paramName];
  };

  // Return all the initial values related to given queryParamNames
  // InitialValues for "amenities" filter could be
  // { amenities: "has_any:towel,jacuzzi" }
  const isArray = Array.isArray(queryParamNames);
  return isArray
    ? queryParamNames.reduce((acc, paramName) => {
      const initValue = getInitialValue(paramName);
      const addInitialValueMaybe = initValue ? { [paramName]: initValue } : {};
      return { ...acc, ...addInitialValueMaybe };
    }, {})
    : {};
};

/**
 * Some parameters could conflict with sort. If sortConfig defines conflictingFilters,
 * This function checks if they are active and returns "sort" param as null
 *
 * @param {*} searchParams
 * @param {*} filterConfigs contains config like listingFieldsConfig and defaultFiltersConfig
 * @param {*} sortConfig
 * @returns sort parameter as null if sortConfig defines conflictingFilters
 */
export const cleanSearchFromConflictingParams = (searchParams, filterConfigs, sortConfig) => {
  // Single out filters that should disable SortBy when an active
  // keyword search sorts the listings according to relevance.
  // In those cases, sort parameter should be removed.
  const sortingFiltersActive = isAnyFilterActive(
    sortConfig.conflictingFilters,
    searchParams,
    filterConfigs
  );

  // search params without category-restricted params
  const unlimitedSearchParams = omitLimitedListingFieldParams(searchParams, filterConfigs);

  return sortingFiltersActive
    ? { ...unlimitedSearchParams, [sortConfig.queryParamName]: null }
    : unlimitedSearchParams;
};

/**
 * Extract search parameters, including a custom URL params,
 * which are validated by mapping the values to marketplace custom config.
 *
 * @param {Object} params Search query params
 * @param {Object} listingFieldsConfig extended data configuration with indexForSearch === true
 * @param {Object} defaultFiltersConfig configuration for default built-in filters.
 * @param {Object} sortConfig config for sort search results feature
 * @param {boolean} isOriginInUse if origin is in use, return it too.
 */
export const pickSearchParamsOnly = (params, filterConfigs, sortConfig, isOriginInUse) => {
  const { address, origin, bounds, ...rest } = params || {};
  const boundsMaybe = bounds ? { bounds } : {};
  const originMaybe = isOriginInUse && origin ? { origin } : {};
  const filterParams = validFilterParams(rest, filterConfigs);
  const sort = rest[sortConfig.queryParamName];
  const sortMaybe = sort ? { sort } : {};

  return {
    ...boundsMaybe,
    ...originMaybe,
    ...filterParams,
    ...sortMaybe,
  };
};

/**
 * This helper has 2 functions:
 * - It picks search params from Location instance (location.search)
 * - It verifies that those search params are the same as search params in state.
 *   In some cases, search params are referencing previous params
 *   and listings should not be considered loaded.
 *
 * @param {Object} searchFromLocation searchParams from URL (location.search)
 * @param {Object} searchParamsInProps searchParams from store
 * @param {Object} listingFieldsConfig config for listing's extended data
 * @param {Object} defaultFiltersConfig config for default filters
 * @param {Object} sortConfig config for SortBy feature
 * @returns object containing
 *   1. searchParamsInURL (omit pagination 'page' or 'mapSearch'),
 *   2. urlQueryParams (picked valid search params for listing query), and
 *   3. searchParamsAreInSync is true if searchFromLocation and searchParamsInProps match.
 */
export const searchParamsPicker = (
  searchFromLocation,
  searchParamsInProps,
  filterConfigs,
  sortConfig,
  isOriginInUse
) => {
  const { mapSearch, page, ...searchParamsInURL } = parse(searchFromLocation, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Pick only search params that are part of current search configuration
  const queryParamsFromSearchParams = pickSearchParamsOnly(
    searchParamsInProps,
    filterConfigs,
    sortConfig,
    isOriginInUse
  );
  // Pick only search params that are part of current search configuration
  const queryParamsFromURL = pickSearchParamsOnly(
    searchParamsInURL,
    filterConfigs,
    sortConfig,
    isOriginInUse
  );

  // Page transition might initially use values from previous search
  const searchParamsAreInSync =
    stringify(queryParamsFromURL) === stringify(queryParamsFromSearchParams);

  return {
    urlQueryParams: queryParamsFromURL,
    searchParamsInURL,
    searchParamsAreInSync,
  };
};

export const pickListingFieldFilters = params => {
  const { listingFields, locationSearch, categoryConfiguration, make, model } = params;
  const searchParams = {
    ...parse(locationSearch),
    ...(make && { pub_categoryLevel1: make }),
    ...(model && { pub_categoryLevel2: model }),
  };
  const categories = categoryConfiguration.categories;
  const validNestedCategoryParamNames = categories
    ? validURLParamForCategoryData(categoryConfiguration.key, categories, 1, searchParams)
    : {};
  const currentCategories = Object.values(validNestedCategoryParamNames);
  const pickedFields = listingFields.reduce((picked, fieldConfig) => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory ? [...picked, fieldConfig] : picked;
  }, []);
  return pickedFields;
};

/**
 * Returns listing fields (extended data configs) grouped into arrays. [primaryConfigArray, secondaryConfigArray]
 * @param {Object} configs listing extended data config
 * @param {Array<String>} activeListingTypes select configs that are marked only for these active listing types
 * @returns Array of grouped arrays. First subarray contains primary configs and the second contains secondary configs.
 */
export const groupListingFieldConfigs = (configs, activeListingTypes) =>
  configs.reduce(
    (grouped, config) => {
      const [primary, secondary] = grouped;
      const { listingTypeConfig = {}, filterConfig } = config;
      const isIndexed = filterConfig?.indexForSearch === true;
      const isActiveListingTypes =
        !listingTypeConfig.limitToListingTypeIds ||
        listingTypeConfig.listingTypeIds.every(lt => activeListingTypes.includes(lt));
      const isPrimary = filterConfig?.group === 'primary';
      return isActiveListingTypes && isIndexed && isPrimary
        ? [[...primary, config], secondary]
        : isActiveListingTypes && isIndexed
          ? [primary, [...secondary, config]]
          : grouped;
    },
    [[], []]
  );

export const createSearchResultSchema = (
  listings,
  mainSearchData,
  intl,
  routeConfiguration,
  config,
  additionalValues
) => {
  const marketplaceName = config.marketplaceName;
  const { address, keywords } = mainSearchData;
  const { make, model, totalItems, tireSize, rimSize } = additionalValues || {};

  // Matched case-insensitively, because the URL segment is the lower-cased id and
  // 14 configured ids are not lower-case. An exact match missed every one of them
  // and fell through to the raw slug, so /annonser/hyundai was titled "hyundai
  // däck, fälgar & kompletta hjul" and /annonser/audi/a3 said "audi a3" — on
  // precisely the URLs the category sitemap submits.
  const categories = config.categoryConfiguration.categories;
  const makeObject = findCategoryBySlug(categories, make);
  const makeName = makeObject?.name ?? make;

  const modelObject = findCategoryBySlug(makeObject?.subcategories, model);
  const modelName = modelObject?.name ?? model;

  // Prefer the true total from pagination; fall back to the loaded page size.
  const count = totalItems != null ? totalItems : listings.length;

  // Car brand: /annonser/make
  // SEO Titel: {{category}} däck, fälgar & kompletta hjul | Wheelplace
  // Meta: Köp nya och begagnade däck, fälgar och kompletta hjul som passar {{brand}}. {{count listings}} annonser till salu från privatpersoner och företag.

  // Car brand and model: /annonser/make/model
  // SEO Titel: {{category}} {{subcategory}} däck, fälgar & kompletta hjul | Wheelplace
  // Meta: Köp nya och begagnade däck, fälgar eller kompletta hjul som passar {{category}} {{subcategory}}. {{count listings}} annonser till salu från privatpersoner och företag.

  let title = '';
  let description = '';

  const tireSizeText = tireSize ? tireSizeLabel(tireSize) : null;
  const rimSizeText = rimSize ? rimSizeLabel(rimSize) : null;

  if (tireSizeText) {
    title = `Däck ${tireSizeText} – köp nya & begagnade | Wheelplace`;
    description = `Sök bland ${count} annonser på däck i dimension ${tireSizeText}. Jämför pris och skick på nya och begagnade däck från privatpersoner och företag på Wheelplace.`;
  } else if (rimSizeText) {
    title = `Fälgar ${rimSizeText} – köp nya & begagnade | Wheelplace`;
    description = `Sök bland ${count} annonser på fälgar ${rimSizeText}. Jämför pris och skick på nya och begagnade fälgar från privatpersoner och företag på Wheelplace.`;
  } else if (makeName) {
    const brandLabel = makeName;
    const modelLabel = modelName ? ` ${modelName}` : '';
    const categoryLabel = makeName;
    const subcategoryLabel = modelName ? ` ${modelName}` : '';

    if (modelName) {
      title = `${categoryLabel}${subcategoryLabel} däck, fälgar & kompletta hjul | Wheelplace`;
      description = `Köp nya och begagnade däck, fälgar eller kompletta hjul som passar ${categoryLabel}${subcategoryLabel}. ${count} annonser till salu från privatpersoner och företag.`;
    } else {
      title = `${brandLabel} däck, fälgar & kompletta hjul | Wheelplace`;
      description = `Köp nya och begagnade däck, fälgar och kompletta hjul som passar ${brandLabel}. ${count} annonser till salu från privatpersoner och företag.`;
    }
  } else {
    const keywordsMaybe = keywords ? `"${keywords}"` : null;
    const searchTitle =
      address || keywordsMaybe || intl.formatMessage({ id: 'SearchPage.schemaForSearch' });
    title = intl.formatMessage({ id: 'SearchPage.schemaTitle' }, { searchTitle, marketplaceName });

    // Dynamic, descriptive meta copy (replaces the old "Visar annonser"
    // placeholder which also leaked into og:description on social shares).
    const searchTerm = address || keywords;
    description = searchTerm
      ? `Sök bland ${count} annonser på begagnade och nya däck, fälgar och kompletta hjul för ${searchTerm}. Hitta hjul som passar din bil på ${marketplaceName}.`
      : `Sök bland ${count} annonser på begagnade och nya däck, fälgar och kompletta hjul. Sök med ditt regnummer och hitta hjul som passar din bil.`;
  }

  // Product/Offer items for the ItemList, built with the same builder the SEO
  // pages use. Money → number conversion happens here, where the currency
  // helper is available.
  const priceItems = listings.map(l => {
    const price = l.attributes?.price;
    const hasPrice = price instanceof Money;
    const { name, params } = listingPageLinkProps(l);
    const pathToItem = createResourceLocatorString(name, routeConfiguration, params);
    return {
      id: l.id.uuid,
      title: l.attributes.title,
      priceValue: hasPrice ? convertMoneyToNumber(price) : null,
      currency: hasPrice ? price.currency : null,
      url: `${config.marketplaceRootURL}${pathToItem}`,
    };
  });

  // Minimum product threshold before indexing
  const indexThreshold = 1;
  const noindex = count < indexThreshold;

  // Hosted translations are edited in the Console and can carry stray spaces.
  // SearchPage.schemaForSearch currently begins with one, and the unfiltered
  // /annonser title is built straight from it, so every such page went out as
  // " Nya och begagnade hjul, däck och fälgar | Wheelplace" — leading space and
  // all — in <title> and og:title. Tidy the finished strings here rather than
  // depend on the content staying neat.
  const tidyText = text => String(text || '').replace(/\s+/g, ' ').trim();
  const tidyTitle = tidyText(title);
  const tidyDescription = tidyText(description);

  return {
    title: tidyTitle,
    description: tidyDescription,
    noindex,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'SearchResultsPage',
      description: tidyDescription,
      name: tidyTitle,
      // A real nested object. This used to be JSON.stringify'd into a string,
      // which meant no search engine could read the listing grid at all.
      mainEntity: itemListSchema(priceItems, config.marketplaceRootURL, { name: tidyTitle }),
    },
  };
};
